/**
 * Pipeline API endpoints
 */

import { apiClient } from './client';

export interface Pipeline {
  key: string;
  name: string;
  status: string;
  active_days?: string[];
  active_hours_from?: number;
  active_hours_to?: number;
  available_at?: string;
  import_rules?: {
    dont_import_open_to_work?: boolean;
    dont_import_less_that_500_connections?: boolean;
    dont_import_no_photo_on_profile?: boolean;
    dont_import_free_linkedin_account?: boolean;
    dont_import_duplicate_prospects_with_same_owner?: boolean;
    delete_other_waiting_prospects_with_same_owner?: boolean;
  };
  stream?: {
    key: string;
    name: string;
  };
  is_template?: boolean;
  created_at: string;
}

export interface PipelineListResponse {
  output: {
    items: Pipeline[];
    next_page_token?: string;
    previous_page_token?: string;
    total_results: number;
  };
}

export const pipelineApi = {
  /**
   * List pipelines for a stream or templates
   */
  getPipelines: async (params?: {
    stream_uuid?: string;
    is_template?: boolean;
    status?: string;
    search?: string;
    page_token?: string;
    max_results?: number;
  }): Promise<PipelineListResponse> => {
    const response = await apiClient.get<PipelineListResponse>('/pipelines/', { params });
    return response.data;
  },

  /**
   * Create a pipeline
   */
  createPipeline: async (data: {
    stream_uuid: string;
    name: string;
    status?: string;
    active_days?: string[];
    active_hours_from?: number;
    active_hours_to?: number;
    template_uuid?: string;
    import_rules?: Pipeline['import_rules'];
    // When true, backend will immediately soft-delete the created pipeline.
    // Used by multi-step wizards that need a draft pipeline to configure steps
    // without exposing it in regular lists until final confirmation.
    mark_deleted?: boolean;
  }): Promise<{ output: Pipeline }> => {
    const response = await apiClient.post<{ output: Pipeline }>('/pipelines/create/', data);
    return response.data;
  },

  /**
   * Update a pipeline
   */
  updatePipeline: async (pipelineUuid: string, data: {
    name?: string;
    status?: string;
    active_days?: string[];
    active_hours_from?: number;
    active_hours_to?: number;
    import_rules?: Pipeline['import_rules'];
    // When true, backend will restore a soft-deleted pipeline. Used by
    // multi-step wizards to make a draft pipeline visible after confirmation.
    restore_deleted?: boolean;
  }): Promise<{ output: Pipeline }> => {
    const response = await apiClient.patch<{ output: Pipeline }>(
      `/pipelines/${pipelineUuid}/update/`,
      data
    );
    return response.data;
  },

  /**
   * Hard-delete a pipeline (used for wizard-created draft pipelines)
   */
  deletePipeline: async (pipelineUuid: string): Promise<void> => {
    await apiClient.delete(`/pipelines/${pipelineUuid}/delete/`);
  },

  /**
   * Get steps with optional filters
   */
  getSteps: async (params?: {
    pipeline_uuid?: string;
    type?: string;
    action?: string;
    event?: string;
    search?: string;
    page_token?: string;
    max_results?: number;
    // When true, backend will allow fetching steps for soft-deleted pipelines.
    // Used only by wizard flows that work with draft (soft-deleted) pipelines.
    include_deleted_pipeline?: boolean;
  }): Promise<{ output: { items: Step[]; next_page_token?: string; previous_page_token?: string; total_results: number } }> => {
    const response = await apiClient.get<{ output: { items: Step[]; next_page_token?: string; previous_page_token?: string; total_results: number } }>(
      '/steps/',
      { params }
    );
    return response.data;
  },
};

export interface Step {
  key: string;
  name: string;
  message_template?: string | null;
  ai_messaging: boolean;
  sequence_order?: number | null;
  type: string;
  action?: string;
  created_at: string;
}


